import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useCookieConsent from '../../hooks/useCookieConsent';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';

const optionCardSx = { p: 2, borderRadius: BRAND_RADIUS.button, border: `1px solid ${BRAND.line}` };

const CookiePreferencesDialog = () => {
  const { t } = useTranslation();
  const {
    consent,
    preferencesOpen,
    isConsentRequired,
    closePreferences,
    savePreferences,
  } = useCookieConsent();
  const [functional, setFunctional] = useState(false);
  const [thirdParty, setThirdParty] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  useEffect(() => {
    if (preferencesOpen) {
      setFunctional(consent?.functional === true);
      setThirdParty(consent?.thirdParty === true);
      setAdvertising(consent?.advertising === true);
    }
  }, [consent, preferencesOpen]);

  return (
    <Dialog
      open={preferencesOpen}
      onClose={(event, reason) => {
        if (isConsentRequired) {
          return;
        }
        closePreferences();
      }}
      disableEscapeKeyDown={isConsentRequired}
      maxWidth="sm"
      fullWidth
      disableScrollLock
    >
      <DialogTitle sx={{ fontWeight: 800 }}>{t('legal.consent.preferencesTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('legal.consent.preferencesDescription')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ p: 2, borderRadius: BRAND_RADIUS.button, bgcolor: brandAlpha('accent', 0.08), border: `1px solid ${brandAlpha('accent', 0.18)}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('legal.consent.necessaryTitle')}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: BRAND.accentText }}>{t('legal.consent.alwaysOn')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">{t('legal.consent.necessaryDescription')}</Typography>
          </Box>

          <Box sx={optionCardSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={functional}
                  onChange={(event) => setFunctional(event.target.checked)}
                  color="secondary"
                />
              }
              label={(
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('legal.consent.functionalTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('legal.consent.functionalDescription')}</Typography>
                </Box>
              )}
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
          </Box>

          <Box sx={optionCardSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={thirdParty}
                  onChange={(event) => setThirdParty(event.target.checked)}
                  color="secondary"
                />
              }
              label={(
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('legal.consent.thirdPartyTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('legal.consent.thirdPartyDescription')}</Typography>
                </Box>
              )}
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
          </Box>

          <Box sx={optionCardSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={advertising}
                  onChange={(event) => setAdvertising(event.target.checked)}
                  color="secondary"
                />
              }
              label={(
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('legal.consent.advertisingTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('legal.consent.advertisingDescription')}</Typography>
                </Box>
              )}
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={closePreferences} sx={{ textTransform: 'none', color: BRAND.accentText }}>
          {isConsentRequired ? t('common.back') : t('common.cancel')}
        </Button>
        <PrimaryActionButton
          onClick={() => savePreferences({ functional, thirdParty, advertising })}
          sx={{ textTransform: 'none' }}
        >
          {t('legal.consent.savePreferences')}
        </PrimaryActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default CookiePreferencesDialog;
